import React from 'react';
import styled, { keyframes, css } from 'styled-components';

export interface BarChartData {
  label: string;
  value: number;
}

export interface BarChartBenchmarkData {
  label: string;
  value?: number | null;
}

interface BarChartProps {
  data: BarChartData[];
  benchmarkData?: BarChartBenchmarkData[];
  multiple?: number;
  percent?: boolean;
  barHeight?: string;
  barPadding?: string;
  fontSize?: string;
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  benchmarkData,
  multiple = 1,
  percent = true,
  barHeight = '14px',
  barPadding = '0px',
  fontSize = '11px',
}) => {
  const benchmarkMap = new Map(
    (benchmarkData || [])
      .filter(item => typeof item?.label === 'string')
      .map(item => [item.label.trim().toLowerCase(), item?.value])
  );

  return (
    <BarContainer barPadding={barPadding}>
      {data?.map((row, index) => {
        const benchmarkValue = benchmarkMap.get(row.label.trim().toLowerCase());
        const hasBenchmark = typeof benchmarkValue === 'number' && Number.isFinite(benchmarkValue);
        const benchmarkPercentage = hasBenchmark
          ? Math.max(0, Math.min(100, benchmarkValue * multiple))
          : null;

        return (
          <React.Fragment key={index}>
            <BarTitle fontSize={fontSize}>{row.label}</BarTitle>
            <Bar barHeight={barHeight}>
              <BarFill percentage={row.value * multiple}>
                <span>
                  {row.value}
                  {percent ? '%' : ''}
                </span>
              </BarFill>
              {benchmarkPercentage !== null && (
                <BenchmarkMarker
                  style={{ left: `${benchmarkPercentage}%` }}
                  title={`Benchmark: ${benchmarkValue}`}
                  data-testid={`benchmark-marker-${index}`}
                />
              )}
            </Bar>
          </React.Fragment>
        );
      })}
    </BarContainer>
  );
};
export default BarChart;

const growAnimation = (percentage: number) => keyframes`
  from {
    width: 0%;
  }
  to {
    width: ${percentage}%;
  }
`;

const BarContainer = styled.div<{ barPadding: string }>`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  align-items: center;
  justify-items: stretch;
  column-gap: 10px;
  row-gap: 6px;
  width: 100%;
  padding: ${props => props.barPadding};
  margin-top: 10px;
`;

const BarTitle = styled.div<{ fontSize: string }>`
  font-family: 'Poppins', sans-serif;
  font-size: ${props => props.fontSize};
  color: #333;
  font-weight: 400;
  white-space: nowrap;
  text-align: right;
  justify-self: end;
`;

const Bar = styled.div<{ barHeight: string }>`
  position: relative;
  height: ${props => props.barHeight};
  width: 100%;
  background-color: #ddd;
  border-radius: 4px;
  overflow: hidden;
  min-width: 0;
  cursor: pointer;
  transition: box-shadow 0.1s ease-in-out;

  &:hover {
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.3);
  }

  &:hover span {
    visibility: visible;
    opacity: 1;
  }
`;

const BarFill = styled.div<{ percentage: number }>`
  height: 100%;
  border-radius: 4px;
  background-color: ${props =>
    props.percentage < 40
      ? 'rgba(255, 50, 90, .4)'
      : props.percentage <= 60
        ? 'rgba(255, 216, 111, 1)'
        : 'rgba(129, 199, 132, .7)'};
  animation: ${props => css`
    ${growAnimation(props.percentage)} 0.8s ease-out
  `};
  animation-fill-mode: forwards;
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;

  span {
    visibility: hidden;
    opacity: 0;
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-size: 10px;
    font-weight: bold;
    transition: opacity 0.1s ease-in-out;
  }
`;

const BenchmarkMarker = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 2px dashed rgba(230, 101, 121, 0.95);
  transform: translateX(-1px);
  pointer-events: none;
`;
